// src/components/.../UploadStepPopup.jsx
import React, { useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Spinner } from "react-bootstrap";
import {
  IconCloudUpload,
  IconFileSpreadsheet,
  IconRocket,
  IconX,
  IconCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import usePostRequest from "../../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../../constant/_constant";
import productTemplate from "../../../assets/templates/CRM-CV-Product-Template.xlsx";
import kitTemplate from "../../../assets/templates/CRM-CV-Kit-Template.xlsx";

const COLORS = {
  primary: "#FE6338",
  primarySoft: "#FFF1EC",
  primaryBorder: "#FFD6C7",
  ink: "#1A1A1A",
  sub: "#6B7280",
  line: "#EDEFF3",
  panel: "#FBFBFC",
  successBg: "#ECFDF5",
  successLine: "#A7F3D0",
  successInk: "#059669",
};

function UploadStepPopupCv({
  show,
  setShow,
  endpoint,
  onUploadSuccess,
  onNext,
  requirementsText,
  rfqId,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [productType, setProductType] = useState("product");

  const { isLoading: uploading, postData: uploadFile } = usePostRequest(
    API_ENDPOINTS.UPLOAD_PRODUCT_CSV,
  );

  const acceptedTypes = ".csv,.xlsx,.xls";

  const templateHref = productType === "kit" ? kitTemplate : productTemplate;
  const templateName =
    productType === "kit"
      ? "CRM-CV-Kit-Template.xlsx"
      : "CRM-CV-Product-Template.xlsx";

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      toast.error("Only CSV or Excel (.xlsx, .xls) files are supported.");
      return;
    }
    setSelectedFile(file);
  };

  const handleInputChange = (e) => handleFileSelect(e.target.files[0]);
  const handleBrowseClick = () => fileInputRef.current.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleClear = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    handleClear();
    setProductType("product");
    setShow(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("type", productType);
    formData.append("rfq_id", rfqId);
    await uploadFile(formData, onSuccessUpload, onErrorUpload);
  };

  const onSuccessUpload = (response) => {
    toast.success(response?.message || "File uploaded successfully!");
    handleClear();
    setShow(false);
    onNext?.(response?.data);
    onUploadSuccess?.(response);
  };

  const onErrorUpload = (error) => {
    toast.error(error?.message || "Error uploading file.");
  };

  const TypeChip = ({ value, label }) => {
    const active = productType === value;
    return (
      <div
        role="button"
        onClick={() => setProductType(value)}
        className="d-flex align-items-center gap-2 px-3 py-2"
        style={{
          borderRadius: 12,
          cursor: "pointer",
          userSelect: "none",
          fontWeight: 600,
          fontSize: 14,
          border: `1.5px solid ${active ? COLORS.primary : COLORS.line}`,
          background: active ? COLORS.primarySoft : "#fff",
          color: active ? COLORS.primary : COLORS.sub,
          transition: "all .15s ease",
        }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center"
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: `1.5px solid ${active ? COLORS.primary : "#C7CDD6"}`,
            background: active ? COLORS.primary : "#fff",
          }}
        >
          {active && <IconCheck size={12} color="#fff" stroke={3} />}
        </span>
        {label}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      contentClassName="border-0 overflow-hidden"
      style={{ fontFamily: "inherit" }}
    >
      <div style={{ borderRadius: 20, overflow: "hidden", background: "#fff" }}>
        {/* Header */}
        <div
          className="d-flex align-items-start justify-content-between px-4"
          style={{ paddingTop: 24, paddingBottom: 20 }}
        >
          <div className="d-flex align-items-center gap-3">
            <span
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: 46,
                height: 46,
                borderRadius: 13,
                background: COLORS.primarySoft,
                color: COLORS.primary,
              }}
            >
              <IconCloudUpload size={24} stroke={1.9} />
            </span>
            <div>
              <div style={{ fontSize: 19, fontWeight: 700, color: COLORS.ink }}>
                Batch Import
              </div>
              <div style={{ fontSize: 13, color: COLORS.sub }}>
                Upload a CSV or Excel file to import your catalog
              </div>
            </div>
          </div>
          <IconX
            size={20}
            role="button"
            onClick={handleClose}
            style={{ color: "#98A2B3", cursor: "pointer", marginTop: 4 }}
          />
        </div>

        <div style={{ height: 1, background: COLORS.line }} />

        <div className="px-4" style={{ paddingTop: 22, paddingBottom: 8 }}>
          <div
            className="mb-4"
            style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}
          >
            Product Type
          </div>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="d-flex gap-2 mb-4" style={{ marginTop: -8 }}>
              <TypeChip value="product" label="Product" />
              <TypeChip value="kit" label="Kit" />
            </div>

            <div>
              <a
                href={templateHref}
                download={templateName}
                className="text-decoration-underline"
                style={{
                  color: "#FE6338",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Download format XLSX file
              </a>
            </div>
          </div>
          <div
            onClick={handleBrowseClick}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              border: `2px dashed ${isDragging ? COLORS.primary : "#DCE0E6"}`,
              borderRadius: 16,
              padding: "44px 24px",
              background: isDragging ? COLORS.primarySoft : COLORS.panel,
              cursor: "pointer",
              transition: "all .2s ease",
            }}
          >
            <span
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: isDragging ? "#fff" : COLORS.primarySoft,
                color: COLORS.primary,
                transition: "all .2s ease",
              }}
            >
              <IconCloudUpload size={30} stroke={1.8} />
            </span>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
              Drag &amp; drop your file here
            </div>
            <div className="mt-1" style={{ fontSize: 13.5, color: COLORS.sub }}>
              or{" "}
              <span style={{ color: COLORS.primary, fontWeight: 700 }}>
                click to browse
              </span>
            </div>
            <div
              className="mt-3 d-flex align-items-center gap-2"
              style={{ fontSize: 12, color: "#98A2B3" }}
            >
              <span
                className="px-2 py-1"
                style={{
                  background: "#fff",
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 6,
                }}
              >
                .CSV
              </span>
              <span
                className="px-2 py-1"
                style={{
                  background: "#fff",
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 6,
                }}
              >
                .XLSX
              </span>
              <span
                className="px-2 py-1"
                style={{
                  background: "#fff",
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 6,
                }}
              >
                .XLS
              </span>
              {/* <span>up to 10MB</span> */}
            </div>
            <input
              type="file"
              accept={acceptedTypes}
              hidden
              ref={fileInputRef}
              onChange={handleInputChange}
            />
          </div>

          {/* Selected file */}
          {selectedFile && (
            <div
              className="d-flex align-items-center justify-content-between gap-2 mt-3 px-3 py-2"
              style={{
                background: COLORS.successBg,
                border: `1px solid ${COLORS.successLine}`,
                borderRadius: 12,
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: "#fff",
                    color: COLORS.successInk,
                  }}
                >
                  <IconFileSpreadsheet size={20} />
                </span>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}
                  >
                    {selectedFile.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: COLORS.successInk,
                      fontWeight: 500,
                    }}
                  >
                    {(selectedFile.size / 1024).toFixed(1)} KB · Ready to upload
                  </div>
                </div>
              </div>
              <IconX
                size={18}
                role="button"
                onClick={handleClear}
                style={{ cursor: "pointer", color: "#94A3B8" }}
              />
            </div>
          )}

          {/* Requirements */}
          <div
            className="d-flex gap-2 mt-3 px-3 py-3"
            style={{
              background: "#F8FAFF",
              border: `1px solid ${COLORS.line}`,
              borderRadius: 12,
              fontSize: 12.5,
              color: COLORS.sub,
              lineHeight: 1.5,
            }}
          >
            <IconInfoCircle
              size={17}
              color={COLORS.primary}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <div>
              <span style={{ fontWeight: 700, color: COLORS.ink }}>
                Requirements:
              </span>{" "}
              {requirementsText ||
                "First row must contain column headers. Required fields: SKU ID, Vendor ID, Aspetto Price, Type."}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="d-flex align-items-center justify-content-end gap-2 px-4"
          style={{ paddingTop: 18, paddingBottom: 22, marginTop: 8 }}
        >
          <Button
            onClick={handleClose}
            className="fw-semibold px-4"
            style={{
              borderRadius: 12,
              background: "#fff",
              border: `1.5px solid ${COLORS.line}`,
              color: COLORS.sub,
              padding: "9px 22px",
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!selectedFile || uploading}
            onClick={handleUpload}
            className="d-flex align-items-center gap-2 fw-semibold px-4 border-0"
            style={{
              borderRadius: 12,
              background:
                !selectedFile || uploading
                  ? "#FEB59D"
                  : `linear-gradient(135deg, ${COLORS.primary}, #FF7A54)`,
              color: "#fff",
              padding: "9px 22px",
              boxShadow:
                !selectedFile || uploading
                  ? "none"
                  : "0 6px 16px rgba(254,99,56,.35)",
            }}
          >
            {uploading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <IconRocket size={17} /> Upload &amp; Continue
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default UploadStepPopupCv;
